// Import documents into the coordination schedule (/schedule-import/:projectId)
//
//   1. Upload   - PDF meeting minutes, CSV / Excel trackers (several at once)
//                 or paste minutes text. Each file is parsed on the server
//                 into tables with a suggested mapping; nothing is written.
//   2. Map      - per table: what it becomes (schedule activities /
//                 milestones, issues / RFIs / action items, checklist items,
//                 meeting minutes, contacts), which column feeds which field,
//                 options (grouping, de-duplication, defaults) and which rows.
//   3. Contractors - the shorthand the documents used for parties (BES, DP,
//                 GG ...) mapped to companies / people on the project. Can be
//                 skipped and done later ("Map contractors" on the schedule).
//   4. Commit   - writes everything, remembers the mapping per table shape
//                 so the next weekly file maps itself, and shows the result.
//
// Re-importing a newer edition of the same tracker updates matching rows
// (source id / title) instead of duplicating them.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
  Alert, Button, Card, Collapse, Descriptions, Empty, Input, List, Modal, Popconfirm, Progress, Result, Select, Space, Steps, Table, Tag, Tooltip, Typography, Upload, message,
} from "antd";
import { CheckCircleOutlined, DeleteOutlined, FileExcelOutlined, FilePdfOutlined, FileTextOutlined, InboxOutlined, ReloadOutlined, TeamOutlined, UploadOutlined } from "@ant-design/icons";
import { url, tokenRef } from "api";
import CoordinationBreadcrumbs from "components/Coordination/CoordinationBreadcrumbs";
import ContractorMappingTable from "components/Coordination/ContractorMappingTable";
import useLastProject from "components/Coordination/useLastProject";
import useProjectAccess from "hooks/useProjectAccess";
import { listGanttItems } from "api/gantt/ganttApi";
import { withActivityPaths } from "helpers/schedule/activityLabel";
import { parseImportDocument, parseImportText, listImports, getImport, deleteImport, commitImport, getImportTargets } from "api/scheduleImport/scheduleImportApi";
import TableMappingCard from "./TableMappingCard";
import "./scheduleImport.css";
import { matchOption } from "components/SearchSelect/searchSelect";

const { Dragger } = Upload;
// What a row's kind reads as. An internal kind never shows as-is: a
// document the server analysed on its own reads "automatic".
const KIND_LABEL = { ai: "automatic" };
const kindLabel = (k) => KIND_LABEL[k] || k;
const ICON = { pdf: <FilePdfOutlined style={{ color: "#cf1322" }} />, xlsx: <FileExcelOutlined style={{ color: "#237804" }} />, xls: <FileExcelOutlined style={{ color: "#237804" }} />, csv: <FileTextOutlined />, txt: <FileTextOutlined />, docx: <FileTextOutlined /> };
const TARGET_LABEL = { schedule: "schedule", issues: "issues", checklist: "checklist", minutes: "minutes", contacts: "contacts", skip: "skip" };
// Commit results carry the created / updated ids per kind (ganttItemIds,
// issueIds, ...); older results carried plain counts. Always show a count.
const countOf = (result, idsKey, countKey) => {
  if (!result) return 0;
  const ids = result[idsKey];
  if (Array.isArray(ids)) return ids.length;
  const n = result[countKey];
  return typeof n === "number" ? n : (Array.isArray(n) ? n.length : 0);
};
const contactsOf = (result) => ((result && result.summary && result.summary.tables) || [])
  .filter((t) => t.target === "contacts").reduce((n, t) => n + (t.created || 0) + (t.updated || 0), 0);

// mode "issues" (Issues page -> Import Excel / CSV): every table that can be
// read as issues becomes issues; schedule/checklist/minutes suggestions are
// left off so an old issue tracker never spills rows onto the schedule.
function initialMapping(document, mode) {
  const tables = {};
  for (const t of document.tables || []) {
    let target = t.suggested?.target || "skip";
    let fields = { ...(t.suggested?.fields || {}) };
    let options = { ...(t.suggested?.options || {}) };
    let enabled = t.enabled !== false;
    if (mode === "issues" && target !== "issues") {
      const alt = t.suggested?.alternatives?.issues;
      const score = (t.suggested?.scores && t.suggested.scores.issues) || 0;
      if (alt && alt.title) {
        // Re-target as issues; only tables that genuinely read like an issue
        // list (several matching columns) start enabled - contact lists,
        // meeting tables and drawing logs stay off unless switched on.
        target = "issues"; fields = { ...alt }; options = {};
        enabled = t.rowCount > 0 && !t.hidden && score >= 3 && !["contacts", "minutes"].includes(t.suggested?.target);
      } else { target = "skip"; enabled = false; }
    }
    tables[t.id] = { enabled, target, fields, options };
  }
  return { tables };
}

// THE IMPORT TAB'S KEYS (2026-10-14): documentImportAdd to upload and
// import, documentImportEdit to map contractors, documentImportDelete to
// forget an import. Read off GET /schedule-import (routes/scheduleImport).
const NO_CAPS = { canAdd: false, canEdit: false, canDelete: false };
const capsOf = (r) => ({ canAdd: !!(r && r.canAdd), canEdit: !!(r && r.canEdit), canDelete: !!(r && r.canDelete) });
function AddGate({ allowed, quiet = false, children }) {
  if (allowed) return children;
  if (quiet) return null;
  return (
    <Alert type="info" showIcon style={{ marginBottom: 12 }}
           message="Uploading documents to import needs the Import documents add permission. Ask a company admin to grant it." />
  );
}

// No :projectId in the URL (drawer entry): pick a project first.
function ProjectPicker() {
  const history = useHistory();
  const mode = (() => { try { return new URLSearchParams(window.location.search).get("mode") || ""; } catch (e) { return ""; } })();
  const [projects, setProjects] = useState(null);
  const [projectId, setProjectId] = useLastProject(projects || []);
  useEffect(() => {
    fetch(`${url}/projects/picker`, { headers: { Authorization: `Bearer ${localStorage.getItem(tokenRef)}` } })
      .then((r) => r.json()).then((b) => setProjects(Array.isArray(b) ? b : ((b && b.projects) || []))).catch(() => setProjects([]));
  }, []);
  return (
    <div style={{ padding: 24, maxWidth: 900, margin: "0 auto" }}>
      <CoordinationBreadcrumbs items={mode === "issues" ? [{ label: "Issues Manager", to: "/issues" }, { label: "Import issues" }] : [{ label: "Import documents" }]} />
      <Typography.Title level={3} style={{ marginBottom: 4 }}>{mode === "issues" ? "Import issues from PDF / Excel / CSV" : "Import documents into a schedule"}</Typography.Title>
      <Typography.Paragraph type="secondary">Choose the project the {mode === "issues" ? "issues" : "documents"} belong to.</Typography.Paragraph>
      <Space wrap>
        <Select filterOption={matchOption} style={{ minWidth: 320 }} showSearch optionFilterProp="label" placeholder={projects === null ? "Loading projects…" : "Select a project"}
          value={projectId || undefined} onChange={setProjectId}
          options={(projects || []).map((p) => ({ value: p._id, label: p.title || p.name || "Untitled" }))} />
        <Button type="primary" disabled={!projectId} onClick={() => history.push(`/schedule-import/${projectId}${mode ? `?mode=${mode}` : ""}`)}>Continue</Button>
      </Space>
    </div>
  );
}

export default function ScheduleImportPage() {
  const { projectId } = useParams();
  if (!projectId) return <ProjectPicker />;
  return <ScheduleImportWizard projectId={projectId} />;
}

function ScheduleImportWizard({ projectId }) {
  const history = useHistory();
  const location = useLocation();
  const mode = (() => { try { return new URLSearchParams(location.search).get("mode") || ""; } catch (e) { return ""; } })();
  // ?importId= (View / Resume on a previous import, or a deep link)
  const urlImportId = (() => { try { return new URLSearchParams(location.search).get("importId") || ""; } catch (e) { return ""; } })();
  const issuesMode = mode === "issues";
  const { access, can, canEditPlan, loading: accessLoading } = useProjectAccess(projectId);
  const [step, setStep] = useState(0);
  const [docs, setDocs] = useState([]);           // [{ importId, document, mapping }]
  const [targets, setTargets] = useState(null);
  const [ganttItems, setGanttItems] = useState([]);
  const [uploading, setUploading] = useState({});   // name -> pct
  const [busy, setBusy] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [history_, setHistory] = useState([]);
  // documentImport* (owner, 2026-10-14): the server's answer, sent with the
  // history list. Until it arrives nothing is offered.
  const [caps, setCaps] = useState(NO_CAPS);
  const [contractorDraft, setContractorDraft] = useState({});
  const [results, setResults] = useState([]);
  const [error, setError] = useState("");
  const [historySearch, setHistorySearch] = useState("");
  // Commit option: every imported line item waits in the Review Center
  // (Imports) before it shows on the schedule / issues / checklists.
  const [reviewFirst, setReviewFirst] = useState(false);

  const [projectTitle, setProjectTitle] = useState("");
  useEffect(() => {
    if (!projectId) return;
    fetch(`${url}/projects/picker`, { headers: { Authorization: `Bearer ${localStorage.getItem(tokenRef)}` } })
      .then((r) => r.json()).then((b) => {
        const p = (Array.isArray(b) ? b : ((b && b.projects) || [])).find((x) => String(x._id) === String(projectId));
        if (p) setProjectTitle(p.title || p.name || "");
      }).catch(() => {});
  }, [projectId]);

  const loadHistory = useCallback(async () => {
    if (!projectId) return;
    try {
      const r = await listImports(projectId);
      setHistory(r.imports || []);
      setCaps(capsOf(r));
    } catch (e) { setCaps(NO_CAPS); }
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return;
    getImportTargets(projectId).then((r) => setTargets(r.targets)).catch(() => {});
    listGanttItems(projectId).then((r) => setGanttItems(withActivityPaths(Array.isArray(r) ? r : (r.items || [])))).catch(() => {});
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Open a previously parsed / committed import: committed ones show their
  // result (step 4), parsed ones resume at the mapping step. Runs whenever
  // ?importId= changes, so View / Resume works while already on this page.
  const openImport = useCallback(async (importId) => {
    if (!importId) return;
    setBusy(true); setError("");
    try {
      const r = await getImport(projectId, importId);
      const imp = r.import;
      if (!imp || !imp.document) { message.error("This import has no parsed document any more"); return; }
      setDocs([{ importId: imp._id, document: imp.document, mapping: imp.mapping || initialMapping(imp.document, mode), committed: imp.status === "committed", result: imp.result }]);
      if (r.targets) setTargets((t) => t || r.targets);
      if (imp.status === "committed") {
        setResults([{ importId: imp._id, fileName: imp.fileName, result: imp.result }]);
        setStep(3);
      } else {
        setResults([]);
        setStep(1);
      }
    } catch (e) { message.error(e.message); }
    finally { setBusy(false); }
  }, [projectId, mode]);
  useEffect(() => {
    if (urlImportId) openImport(urlImportId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlImportId]);
  const viewImport = (importId) => {
    const qs = new URLSearchParams(location.search);
    qs.set("importId", importId);
    if (String(urlImportId) === String(importId)) openImport(importId); // same link again: just reload it
    else history.push(`/schedule-import/${projectId}?${qs.toString()}`);
  };
  const clearImportLink = () => {
    const qs = new URLSearchParams(location.search);
    if (!qs.has("importId")) return;
    qs.delete("importId");
    history.replace(`/schedule-import/${projectId}${qs.toString() ? `?${qs.toString()}` : ""}`);
  };

  useEffect(() => {
    if (accessLoading || !access) return;
    const allowed = issuesMode ? (can.issueCreate && access.isOwnerCompany) : canEditPlan;
    if (!allowed) {
      message.warning(issuesMode ? "Importing issues needs the issueCreate permission on the project-owner company" : "Only the project-owner company can import documents into the schedule");
      history.replace(issuesMode ? "/issues" : `/coordination/${projectId}`);
    }
  }, [accessLoading, access, canEditPlan, can, issuesMode, history, projectId]);

  // -------------------------------------------------------------- upload
  const addFiles = async (files) => {
    setError("");
    for (const file of files) {
      setUploading((u) => ({ ...u, [file.name]: 0 }));
      try {
        const r = await parseImportDocument(projectId, file, (pct) => setUploading((u) => ({ ...u, [file.name]: pct })));
        if (r.targets && !targets) setTargets(r.targets);
        setDocs((d) => [...d, { importId: r.importId, document: r.document, mapping: initialMapping(r.document, mode) }]);
        message.success(`${file.name}: ${r.document.tables.length} table${r.document.tables.length === 1 ? "" : "s"} found`);
      } catch (e) {
        message.error(`${file.name}: ${e.message}`);
      } finally {
        setUploading((u) => { const n = { ...u }; delete n[file.name]; return n; });
      }
    }
    loadHistory();
  };

  const addPasted = async () => {
    if (!pasteText.trim()) return;
    setBusy(true);
    try {
      const r = await parseImportText(projectId, pasteText, "pasted-minutes.txt");
      if (r.targets && !targets) setTargets(r.targets);
      setDocs((d) => [...d, { importId: r.importId, document: r.document, mapping: initialMapping(r.document, mode) }]);
      setPasteOpen(false); setPasteText("");
    } catch (e) { message.error(e.message); }
    finally { setBusy(false); }
  };

  const removeDoc = async (importId) => {
    try { await deleteImport(projectId, importId); } catch (e) { /* committed ones stay */ }
    setDocs((d) => d.filter((x) => x.importId !== importId));
    loadHistory();
  };

  // ------------------------------------------------------------- mapping
  const setTableMapping = (importId, tableId, tmap) => {
    setDocs((d) => d.map((x) => (x.importId === importId ? { ...x, mapping: { ...x.mapping, tables: { ...x.mapping.tables, [tableId]: tmap } } } : x)));
  };

  const validation = useMemo(() => {
    const problems = [];
    let enabledCount = 0;
    for (const d of docs) {
      for (const t of d.document.tables || []) {
        const m = d.mapping.tables[t.id];
        if (!m || m.enabled === false || !m.target || m.target === "skip") continue;
        enabledCount += 1;
        const req = ((targets && targets[m.target] && targets[m.target].fields) || []).filter((f) => f.required);
        for (const f of req) if (!m.fields[f.key]) problems.push(`${d.document.fileName} · ${t.name}: map "${f.label}"`);
      }
    }
    return { problems, enabledCount };
  }, [docs, targets]);

  const visibleHistory = useMemo(() => {
    const q = historySearch.trim().toLowerCase();
    if (!q) return history_;
    return history_.filter((r) => [
      r.fileName, r.fileType, r.sourceKind, r.status,
      ...(r.tables || []).map((t) => `${t.name} ${t.target}`),
      r.createdAt ? new Date(r.createdAt).toLocaleString() : "",
    ].filter(Boolean).join(" ").toLowerCase().includes(q));
  }, [history_, historySearch]);

  const labelsInFiles = useMemo(() => {
    const set = new Set();
    for (const d of docs) for (const l of d.document.contractorLabels || []) set.add(l.label);
    return [...set];
  }, [docs]);

  // -------------------------------------------------------------- commit
  const commitAll = async () => {
    setBusy(true); setError("");
    const out = [];
    try {
      const contractorMap = {};
      for (const [label, v] of Object.entries(contractorDraft)) if (v && (v.companyId || v.userId)) contractorMap[label] = v;
      for (const d of docs) {
        if (d.committed) { out.push({ importId: d.importId, fileName: d.document.fileName, result: d.result, skipped: true }); continue; }
        try {
          const r = await commitImport(projectId, d.importId, d.mapping, contractorMap, { reviewFirst });
          out.push({ importId: d.importId, fileName: d.document.fileName, result: r.result });
        } catch (e) {
          out.push({ importId: d.importId, fileName: d.document.fileName, error: e.message });
        }
      }
      setResults(out);
      setDocs((ds) => ds.map((d) => ({ ...d, committed: true })));
      setStep(3);
      loadHistory();
    } finally { setBusy(false); }
  };

  // -------------------------------------------------------------- render
  const stepItems = [
    { title: "Upload" }, { title: "Map & preview" }, { title: "Contractors" }, { title: "Done" },
  ];

  const summaryOfDoc = (d) => {
    const counts = {};
    for (const t of d.document.tables || []) {
      const m = d.mapping.tables[t.id];
      if (!m || m.enabled === false || !m.target || m.target === "skip") continue;
      counts[m.target] = (counts[m.target] || 0) + (Array.isArray(m.selectedRows) ? m.selectedRows.length : t.rows.filter((r) => r.selected !== false).length);
    }
    return counts;
  };

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: "0 auto" }}>
      <CoordinationBreadcrumbs items={issuesMode
        ? [{ label: "Issues Manager", to: "/issues" }, { label: `Import issues - ${projectTitle || "project"}` }]
        : [{ label: projectTitle || "Project", to: `/coordination/${projectId}` }, { label: "Import documents" }]} />
      <Typography.Title level={3} style={{ marginBottom: 4 }}>{issuesMode ? "Import issues from PDF / Excel / CSV" : "Import documents into the schedule"}</Typography.Title>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
        {issuesMode ? (
          <>Issue trackers, RFI logs, punch lists and action lists kept in Excel / CSV, PDFs (meeting minutes, weekly job reviews / look-aheads), BCF files exported from Revit / Navisworks / Solibri, XML and JSON exports become issues on <b>{projectTitle}</b>:
            map the columns (title, id, dates, status, priority, answer, comments, level / area / grid, responsible), pick the rows, and commit.
            Rows with a source id are updated on re-import instead of duplicated. Nothing is written until you confirm the mapping.</>
        ) : (
          <>PDF meeting minutes, CSV and Excel trackers become schedule activities and milestones, issues / RFIs / action items,
            checklists and meeting minutes on <b>{projectTitle}</b>. Nothing is written until you confirm the mapping.</>
        )}
      </Typography.Paragraph>
      <Steps current={step} items={stepItems} size="small" style={{ marginBottom: 20 }} onChange={(s) => { if (s < step || (s === 1 && docs.length) || (s === 2 && docs.length && !validation.problems.length)) setStep(s); }} />

      {step === 0 && (
        <>
          <AddGate allowed={caps.canAdd}>
          <Dragger
            multiple accept=".pdf,.csv,.tsv,.txt,.md,.docx,.xlsx,.xlsm,.xls,.xml,.xer,.mpp,.bcf,.bcfzip,.json"
            showUploadList={false}
            beforeUpload={(file, list) => { if (file === list[0]) addFiles(list); return false; }}
            style={{ marginBottom: 12 }}
          >
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">Drop files here, or click to choose</p>
            <p className="ant-upload-hint">PDF meeting minutes · CSV / Excel trackers (priority lists, issue trackers, RFI logs, project passports, MoM sheets) · MS Project / P6 schedules (.xml, .xer) · BCF issues (.bcf/.bcfzip from Revit, Navisworks, Solibri) · JSON · up to 250 MB each. Several files at once are fine.</p>
          </Dragger>
          </AddGate>
          <Space wrap style={{ marginBottom: 12 }}>
            <AddGate allowed={caps.canAdd} quiet>
              <Button icon={<FileTextOutlined />} onClick={() => setPasteOpen(true)}>Paste minutes text</Button>
            </AddGate>
            {docs.length > 0 && <Button type="primary" onClick={() => setStep(1)}>Continue to mapping ({docs.length} file{docs.length === 1 ? "" : "s"})</Button>}
          </Space>
          {Object.keys(uploading).length > 0 && (
            <Card size="small" style={{ marginBottom: 12 }}>
              {Object.entries(uploading).map(([name, pct]) => (
                <div key={name} style={{ marginBottom: 6 }}>
                  <Typography.Text style={{ fontSize: 12 }}>{name}{pct >= 100 ? " - parsing…" : ""}</Typography.Text>
                  <Progress percent={pct} size="small" status={pct >= 100 ? "active" : "normal"} />
                </div>
              ))}
            </Card>
          )}
          {docs.length > 0 && (
            <List
              size="small" bordered style={{ marginBottom: 16, background: "#fff" }}
              header={<b>Parsed in this session</b>}
              dataSource={docs}
              renderItem={(d) => (
                <List.Item actions={[<a key="rm" onClick={() => removeDoc(d.importId)}><DeleteOutlined /> remove</a>]}>
                  <Space wrap>
                    {ICON[d.document.fileType] || <FileTextOutlined />}
                    <b>{d.document.fileName}</b>
                    <Tag>{kindLabel(d.document.sourceKind)}</Tag>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {d.document.tables.length} table{d.document.tables.length === 1 ? "" : "s"} · {d.document.tables.reduce((n, t) => n + t.rowCount, 0)} rows
                      {d.document.mediaCount ? ` · ${d.document.mediaCount} embedded images` : ""}
                      {d.document.contractorLabels?.length ? ` · labels: ${d.document.contractorLabels.slice(0, 6).map((l) => l.label).join(", ")}` : ""}
                    </Typography.Text>
                    {d.document.tables.map((t) => <Tag key={t.id} color={t.enabled ? "blue" : "default"}>{t.name}: {TARGET_LABEL[t.suggested?.target] || "skip"}</Tag>)}
                  </Space>
                </List.Item>
              )}
            />
          )}
          <Card size="small" title="Previous imports on this project"
            extra={<Space>
              {history_.length > 0 && (
                <Input.Search allowClear size="small" placeholder="Search imports (file, kind, status, table…)" style={{ width: 280 }}
                  value={historySearch} onChange={(e) => setHistorySearch(e.target.value)} />
              )}
              <Button size="small" icon={<ReloadOutlined />} onClick={loadHistory}>Refresh</Button>
            </Space>}>
            {history_.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No imports yet" /> : (
              <Table
                size="small" rowKey="_id" pagination={{ pageSize: 8, size: "small" }} dataSource={visibleHistory}
                locale={{ emptyText: `No imports match "${historySearch.trim()}"` }}
                columns={[
                  { title: "File", dataIndex: "fileName", render: (v, r) => <Space>{ICON[r.fileType] || <FileTextOutlined />}<span>{v}</span></Space> },
                  { title: "Kind", dataIndex: "sourceKind", width: 170, render: (v) => <Tag>{kindLabel(v)}</Tag> },
                  { title: "Status", dataIndex: "status", width: 100, render: (v) => <Tag color={v === "committed" ? "green" : v === "failed" ? "red" : "gold"}>{v}</Tag> },
                  { title: "Tables", key: "tables", render: (_, r) => (r.tables || []).map((t) => <Tag key={t.id}>{t.name}: {t.target}</Tag>) },
                  { title: "Result", key: "result", width: 280, render: (_, r) => r.result ? <span style={{ fontSize: 12 }}>{r.result.created || 0} created · {r.result.updated || 0} updated · {r.result.skipped || 0} skipped{Array.isArray(r.result.errors) ? (r.result.errors.length ? ` · ${r.result.errors.length} errors` : "") : (r.result.errors ? ` · ${r.result.errors} errors` : "")}<br />{countOf(r.result, "ganttItemIds", "ganttItems")} activities · {countOf(r.result, "issueIds", "issues")} issues · {countOf(r.result, "checklistIds", "checklists")} checklist · {countOf(r.result, "minuteIds", "minutes")} minutes{contactsOf(r.result) ? ` · ${contactsOf(r.result)} contacts` : ""}</span> : null },
                  { title: "When", dataIndex: "createdAt", width: 150, render: (v) => new Date(v).toLocaleString() },
                  {
                    title: "", key: "a", width: 150, render: (_, r) => (
                      <Space size={4}>
                        <Button size="small" loading={busy && String(urlImportId) === String(r._id)} onClick={() => viewImport(r._id)}>{r.status === "committed" ? "View" : "Resume"}</Button>
                        {r.status !== "committed" && caps.canDelete && (
                          <Popconfirm title="Forget this parsed import?" onConfirm={async () => { await deleteImport(projectId, r._id).catch(() => {}); loadHistory(); }}>
                            <Button size="small" danger icon={<DeleteOutlined />} />
                          </Popconfirm>
                        )}
                      </Space>
                    ),
                  },
                ]}
              />
            )}
          </Card>
        </>
      )}

      {step === 1 && (
        <>
          {!targets && <Alert type="info" showIcon message="Loading mapping vocabulary…" style={{ marginBottom: 8 }} />}
          {docs.map((d) => (
            <Card key={d.importId} size="small" style={{ marginBottom: 12 }}
              title={<Space>{ICON[d.document.fileType] || <FileTextOutlined />}<span>{d.document.fileName}</span><Tag>{kindLabel(d.document.sourceKind)}</Tag>
                {Object.entries(summaryOfDoc(d)).map(([k, n]) => <Tag key={k} color="blue">{n} → {k}</Tag>)}</Space>}
              extra={d.committed ? <Tag color="green">already committed</Tag> : null}>
              {d.document.info && d.document.info.length > 0 && (
                <Descriptions size="small" column={3} style={{ marginBottom: 8 }} items={d.document.info.slice(0, 9).map((p, i) => ({ key: i, label: p.key, children: String(p.value).slice(0, 80) }))} />
              )}
              {d.document.contractorLabels && d.document.contractorLabels.length > 0 && (
                <div style={{ marginBottom: 8, fontSize: 12 }}>
                  <TeamOutlined /> Contractor labels in this file:{" "}
                  {d.document.contractorLabels.map((l) => (
                    <Tooltip key={l.label} title={`${l.count} mention(s) via ${l.sources.join(", ")}${l.resolved && l.resolved.companyId ? ` · mapped to ${l.resolved.companyName || l.resolved.companyId}` : " · not mapped yet"}`}>
                      <Tag color={l.resolved && l.resolved.companyId ? "green" : "gold"}>{l.label}</Tag>
                    </Tooltip>
                  ))}
                  <Typography.Text type="secondary"> (mapped in the next step)</Typography.Text>
                </div>
              )}
              {targets && (d.document.tables || []).map((t) => (
                <TableMappingCard key={t.id} table={t} tmap={d.mapping.tables[t.id] || { enabled: false, target: "skip", fields: {} }} targets={targets}
                  ganttItems={ganttItems} onChange={(m) => setTableMapping(d.importId, t.id, m)} />
              ))}
              {(d.document.tables || []).length === 0 && <Empty description="No tables were recognised in this file" />}
            </Card>
          ))}
          {validation.problems.length > 0 && <Alert type="warning" showIcon style={{ marginBottom: 8 }} message="Some enabled tables are missing a required field" description={<ul style={{ margin: 0, paddingLeft: 18 }}>{validation.problems.map((p) => <li key={p}>{p}</li>)}</ul>} />}
          <Space>
            <Button onClick={() => setStep(0)}>Back</Button>
            <Button type="primary" disabled={!validation.enabledCount || validation.problems.length > 0} onClick={() => setStep(2)}>
              Continue to contractors
            </Button>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>{validation.enabledCount} table{validation.enabledCount === 1 ? "" : "s"} will be imported</Typography.Text>
          </Space>
        </>
      )}

      {step === 2 && (
        <>
          <Card size="small" title="Map contractors" style={{ marginBottom: 12 }}
            extra={<Typography.Text type="secondary" style={{ fontSize: 12 }}>Optional - you can also do this later from the schedule ("Map contractors")</Typography.Text>}>
            <ContractorMappingTable projectId={projectId} canEdit={!!can.ganttEdit || !!can.ganttAdd} highlightLabels={labelsInFiles}
              onLocalChange={setContractorDraft} />
            <Alert type="info" showIcon style={{ marginTop: 8 }} message={<span style={{ fontSize: 12 }}>
              Choices made here (saved or not) are applied when you import. Labels that stay unmapped keep their shorthand on each row and can be mapped any time -
              the mapping then applies to everything already imported and to future imports.
            </span>} />
          </Card>
          <Card size="small" style={{ marginBottom: 12 }}>
            <label style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer" }}>
              <input type="checkbox" checked={reviewFirst} onChange={(e) => setReviewFirst(e.target.checked)} style={{ marginTop: 3 }} />
              <span>
                <b>Review the imported line items before they go live</b>
                <div style={{ fontSize: 12, color: "#666" }}>Every new activity, issue and checklist item from this import waits in the Review Center (Imports tab) where you approve or reject each line - nothing appears on the schedule, in the issues list or in the emailed snapshot until approved. Updates to rows that already exist apply right away.</div>
              </span>
            </label>
          </Card>
          <Space>
            <Button onClick={() => setStep(1)}>Back</Button>
            <Button type="primary" icon={<UploadOutlined />} loading={busy} onClick={commitAll} disabled={!caps.canAdd}>
              Import {docs.filter((d) => !d.committed).length} file{docs.filter((d) => !d.committed).length === 1 ? "" : "s"} now{reviewFirst ? " (to review)" : ""}
            </Button>
          </Space>
        </>
      )}

      {step === 3 && (
        <>
          <Result
            status={results.some((r) => r.error) ? "warning" : "success"}
            icon={<CheckCircleOutlined />}
            title={results.some((r) => r.error) ? "Import finished with problems" : "Import finished"}
            subTitle={`${results.reduce((n, r) => n + (r.result?.created || 0), 0)} created · ${results.reduce((n, r) => n + (r.result?.updated || 0), 0)} updated · ${results.reduce((n, r) => n + (r.result?.skipped || 0), 0)} skipped`}
            extra={[
              <Button key="issues" type={issuesMode ? "primary" : "default"} onClick={() => history.push(`/issues?projectId=${projectId}`)}>Issues Manager</Button>,
              <Button key="sched" type={issuesMode ? "default" : "primary"} onClick={() => history.push(`/scheduler/${projectId}`)}>Open schedule</Button>,
              <Button key="dash" onClick={() => history.push(`/coordination/${projectId}`)}>Dashboard</Button>,
              ...(reviewFirst ? [<Button key="review" type="primary" onClick={() => history.push(`/approval-queue?projectId=${projectId}`)}>Review imported items</Button>] : []),
              <Button key="cl" onClick={() => history.push(`/checklists?projectId=${projectId}`)}>Checklists</Button>,
              <Button key="more" onClick={() => { setDocs([]); setResults([]); setStep(0); clearImportLink(); }}>Import more files</Button>,
            ]}
          />
          {results.map((r) => (
            <Card key={r.importId} size="small" title={r.fileName} style={{ marginBottom: 8 }} extra={r.error ? <Tag color="red">failed</Tag> : <Tag color="green">done</Tag>}>
              {r.error && <Alert type="error" showIcon message={r.error} />}
              {r.result && (
                <>
                  <Descriptions size="small" column={5} items={[
                    { key: "a", label: "Schedule activities / milestones", children: countOf(r.result, "ganttItemIds", "ganttItems") },
                    { key: "b", label: "Issues", children: countOf(r.result, "issueIds", "issues") },
                    { key: "c", label: "Checklist items", children: countOf(r.result, "checklistIds", "checklists") },
                    { key: "d", label: "Meeting minutes", children: countOf(r.result, "minuteIds", "minutes") },
                    { key: "e", label: "Contacts", children: contactsOf(r.result) },
                  ]} />
                  {r.result.summary?.tables && (
                    <Table size="small" rowKey="id" pagination={false} style={{ marginTop: 8 }} dataSource={r.result.summary.tables}
                      columns={[
                        { title: "Table", dataIndex: "name" }, { title: "Became", dataIndex: "target", render: (v) => <Tag>{v}</Tag> },
                        { title: "Created", dataIndex: "created", width: 90 }, { title: "Updated", dataIndex: "updated", width: 90 }, { title: "Skipped", dataIndex: "skipped", width: 90 }, { title: "Errors", dataIndex: "errors", width: 90 },
                      ]} />
                  )}
                  {r.result.summary?.applied?.length > 0 && (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>Contractor mapping applied: {r.result.summary.applied.map((a) => `${a.label} → ${a.items} rows / ${a.issues} issues`).join("; ")}</Typography.Text>
                  )}
                  {r.result.errors && r.result.errors.length > 0 && (
                    <Collapse size="small" style={{ marginTop: 8 }} items={[{ key: "e", label: `${r.result.errors.length} row error(s)`, children: <ul style={{ fontSize: 12 }}>{r.result.errors.slice(0, 50).map((e, i) => <li key={i}>{e.table} row {e.row}: {e.err}</li>)}</ul> }]} />
                  )}
                </>
              )}
            </Card>
          ))}
        </>
      )}

      <Modal open={pasteOpen} onCancel={() => setPasteOpen(false)} onOk={addPasted} okText="Parse" confirmLoading={busy} title="Paste meeting minutes" width={720}>
        <Input.TextArea rows={14} value={pasteText} onChange={(e) => setPasteText(e.target.value)}
          placeholder={"August 7, 2026\nNew Items\n- Fly through clashes\no XC Utilities – Bulk gas rack at G/32 ...\no Pilot Line - COMPLETE\n▪ Mains Install date 7/15/26\n▪ IFF date 7/17/26"} />
      </Modal>
      {error && <Alert type="error" showIcon message={error} style={{ marginTop: 8 }} />}
    </div>
  );
}
